/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, MenuItem, SelectChangeEvent, Typography, useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StyledSelect, styledSelectMenuProps } from './StyledFormInputs';
import { QuestionOption } from './models/Question';

type GroupDropdownComponentProps = {
  subTitle?: string;
  options?: QuestionOption[];
  onChange?: (...event: unknown[]) => void;
  defaultValues?: QuestionOption[];
  borderStyle?: boolean;
  calledFromOnboarding?: boolean;
  ['data-testid']?: string;
};

export default function GroupDropdownComponent({
  subTitle,
  options,
  onChange,
  defaultValues,
  borderStyle,
  calledFromOnboarding,
  'data-testid': dataTestId,
}: GroupDropdownComponentProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const [option1, setOption1] = useState<QuestionOption | undefined>(defaultValues?.find(
    (item) => item.order === 1,
  ));
  const [option2, setOption2] = useState<QuestionOption | undefined>(defaultValues?.find(
    (item) => item.order === 2,
  ));

  const optionsAvailable1 = options;
  const optionsAvailable2 =
    useMemo(() => options?.filter((item) => item.value !== option1?.value), [option1]);

  // OSP-1955 - we hide the second buisness goals dropdown in the onboarding form
  const hideSecondDropdown = subTitle === 'Another area I want to improve is' && window.location.href.includes('onboarding/form');

  const reOrderOptions =
    () => {
      // OSP-1955 - if hideSecondDropdown, make sure we only submit the first option
      if (hideSecondDropdown) {
        return [
          ...(option1?.value ? [{ ...option1, order: 1 }] : []),
        ];
      }

      const optionsArray = Array.isArray(defaultValues) && defaultValues.length !== 0 ?
        defaultValues : options;
      const tempResult = optionsArray?.filter((item) => item.value !== option1?.value &&
        item.value !== option2?.value);
      if (option1) {
        tempResult?.unshift(option1);
      }
      if (option2) {
        tempResult?.splice(1, 0, option2);
      }

      // set the new "order"
      const result = tempResult?.map((item: QuestionOption, index: number) => (
        {
          display: item.display,
          value: item.value,
          order: index + 1,
        }
      ));
      return result;
    };

  useEffect(() => {
    // dispatch new order to parent component
    if (onChange && option1) {
      onChange(reOrderOptions());
    }
  }, [option1, option2]);

  return (
    <Box
      sx={{
        minWidth: 275,
        width: '100%',
      }}
    >
      <StyledSelect
        displayEmpty
        defaultValue={defaultValues?.find(
          (item) => item.order === 1,
        )?.value || ''}
        onChange={(e: SelectChangeEvent<any>) => {
          setOption1(options?.find((option) => option.value === e.target.value));
          if (option2?.value === e.target.value) {
            setOption2(undefined);
          }
        }
        }
        inputProps={{
          sx: {
            borderRadius: '13px !important',
          },
        }}
        MenuProps={styledSelectMenuProps}
        data-testid={dataTestId}
      >
        <MenuItem value='' disabled>{t('onboarding.common.selectPlaceholder')}</MenuItem>
        {optionsAvailable1?.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.display}
          </MenuItem>
        ))}
      </StyledSelect>

      { !hideSecondDropdown && (
        <>
          <Typography sx={{
            mt: theme.spacing(2.5),
            textAlign: 'left',
            mb: theme.spacing(0.625),
            ...(!calledFromOnboarding ? {
              mt: 3,
              mb: '2px',
              color: theme.palette.grey[500],
            } : {}),
          }}>
            {subTitle}
          </Typography>
          <StyledSelect
            displayEmpty
            defaultValue={defaultValues?.find(
              (item) => item.order === 2,
            )?.value || ''}
            value={option2?.value || ''}
            onChange={(e: SelectChangeEvent<any>) => setOption2(
              options?.find(
                (option) => option.value === e.target.value,
              ),
            )}
            inputProps={{
              sx: {
                borderRadius: '13px !important',
              },
            }}
            sx={{
              ...(calledFromOnboarding ? {
                paddingTop: theme.spacing(0),
                paddingBottom: theme.spacing(0),
                // color: theme.palette.color20.main,
                '& .MuiSelect-select': {
                  padding: theme.spacing(1.875),
                },
              } : {}),
            }}
            MenuProps={styledSelectMenuProps}
          >
            <MenuItem value='' disabled>{t('onboarding.common.selectPlaceholder')}</MenuItem>
            {optionsAvailable2?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.display}
              </MenuItem>
            ))}
          </StyledSelect>
        </>)}
    </Box>
  );
}
